import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog"

export function DialogRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: Dialog root is a portal-emitting provider;
  // the content may not be in the DOM when closed. Surface atom contract
  // on a thin wrapping span that is always in the tree.
  // Note: `element.variant === "open"` is a legacy open-state marker.
  const atomDefaults = useAtomDefaults("dialog")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const isOpen = element.variant === "open"
  // defaultOpen: uncontrolled force-open (preview / initial-open use cases).
  // Matches alert-dialog-renderer. Orthogonal to variant="open" which is a
  // controlled open-state marker that collides with matrix variant overrides
  // in DS Browser previews.
  const defaultOpen = (element as unknown as Record<string, unknown>).defaultOpen === true
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  // Type-based: find trigger/content by type, fallback to flat children for legacy
  const allChildren = element.children ?? []
  const triggerChild = allChildren.find(c => c.type === "dialog-trigger")
  const contentWrapper = allChildren.find(c => c.type === "dialog-content")
  const dialogBody = contentWrapper?.children ?? allChildren
  const dialogTitle = contentWrapper?.text
    ? String(resolveSymbol(contentWrapper.text) ?? "")
    : text
  const dialogClassName = contentWrapper?.className

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        dispatchEvent(element.id, "close", {})
      }
    },
    [element.id, dispatchEvent],
  )

  return (
    <span
      data-atom-type="dialog"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-testid={`dialog-${element.id}`}
    >
      <Dialog
        {...(isOpen
          ? { open: true }
          : defaultOpen
            ? { defaultOpen: true }
            : { open: false })}
        onOpenChange={handleOpenChange}
      >
        {triggerChild && (
          <DialogTrigger>
            <div className="cursor-pointer">
              <ElementRenderer element={triggerChild} />
            </div>
          </DialogTrigger>
        )}
        <DialogContent
          className={cn(
            stripPresetConflicts(dialogClassName ?? element.className),
          )}
          data-testid={`dialog-content-${element.id}`}
        >
          {dialogTitle && (
            <DialogTitle>
              {dialogTitle}
            </DialogTitle>
          )}
          {dialogBody.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </DialogContent>
      </Dialog>
    </span>
  )
}
